import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@components/Container';
import Button from '@components/Button';
import { useAuth } from '@lib/useAuth';
import { getUserSessions, type BreathingSession } from '@lib/sessions';
import { computeStreaks } from '@lib/streaks';

type ChartGranularity = 'day' | 'month' | 'year';
type ChartType = 'bar' | 'line';

const GRANULARITY_OPTIONS: ReadonlyArray<{
  id: ChartGranularity;
  label: string;
}> = [
  { id: 'day', label: 'Day' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' },
];

interface NormalizedSession {
  date: Date;
  dateKey: string;
  seconds: number;
}

function TimelineLineChart({ data }: { data: ChartPoint[] }) {
  const strokeId = useId();
  const fillId = `${strokeId}-fill`;

  if (data.length === 0) return null;
  const height = MAX_BAR_HEIGHT;
  const pointWidth = 48;
  const width = Math.max(300, data.length * pointWidth);

  const maxMinutes = Math.max(...data.map((p) => p.minutes), 1);
  const paddingTop = 12;
  const paddingBottom = 18;
  const usableHeight = height - paddingTop - paddingBottom;

  const coords = data.map((p, i) => {
    const x = data.length === 1 ? width / 2 : (i / (data.length - 1)) * width;
    const y = paddingTop + (1 - p.minutes / maxMinutes) * usableHeight;
    return { x, y };
  });

  const linePath = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(2)},${c.y.toFixed(2)}`)
    .join(' ');
  const areaPath = `${linePath} L ${width},${height} L0,${height} Z`;

  return (
    <div
      className="-mx-2 min-w-0 max-w-full overflow-x-auto px-2 pb-3 sm:pb-4"
      style={{ minHeight: `${MAX_BAR_HEIGHT + 20}px`, maxWidth: '100%' }}
    >
      <div style={{ width }}>
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
          <defs>
            <linearGradient id={strokeId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#5FC5D6" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
            <linearGradient id={fillId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#5FC5D6" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${fillId})`} stroke="none" />
          <path
            d={linePath}
            fill="none"
            stroke={`url(#${strokeId})`}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {coords.map((c, i) => (
            <circle
              key={`${c.x}-${i}`}
              cx={c.x}
              cy={c.y}
              r={3}
              fill="#7C3AED"
              stroke="#fff"
              strokeWidth={0.6}
            />
          ))}
        </svg>
      </div>
      <div className="mt-2" style={{ width }}>
        <div className="flex">
          {data.map((d) => (
            <div
              key={d.dateKey}
              className="flex-0 min-w-[48px] text-center text-[11px] text-gray-600"
              title={`${d.fullLabel} · ${formatMinutesShort(d.minutes)}`}
            >
              {d.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface ChartPoint {
  dateKey: string;
  label: string;
  fullLabel: string;
  minutes: number;
}

interface WeeklyPoint {
  weekStart: string;
  minutes: number;
}

const MAX_BAR_HEIGHT = 220;

function Stats() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<BreathingSession[]>([]);
  const [granularity, setGranularity] = useState<ChartGranularity>('day');
  const [chartType, setChartType] = useState<ChartType>('line');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (authLoading) return;

    if (!user) {
      setSessions([]);
      setLoading(false);
      setError(null);
      return;
    }
    const userId = user.id;

    async function fetchSessions() {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserSessions(userId);
        if (!cancelled) {
          setSessions(data);
        }
      } catch (fetchError) {
        if (!cancelled) {
          const message =
            fetchError instanceof Error
              ? fetchError.message
              : 'Unable to load stats right now.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchSessions();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  const normalizedSessions = useMemo<NormalizedSession[]>(() => {
    if (!sessions || sessions.length === 0) return [];

    return sessions
      .map((session) => {
        const rawDate = session.session_date || session.created_at;
        if (!rawDate) return null;
        const date = new Date(rawDate);
        if (Number.isNaN(date.getTime())) return null;

        return {
          date,
          dateKey: formatDateKey(date),
          seconds: Number(session.total_duration) || 0,
        };
      })
      .filter((value): value is NormalizedSession => Boolean(value))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [sessions]);

  const daySet = useMemo(() => buildDaySet(sessions), [sessions]);

  const dayTotalsMap = useMemo(() => {
    const map = new Map<string, number>();
    normalizedSessions.forEach((session) => {
      const existing = map.get(session.dateKey) ?? 0;
      map.set(session.dateKey, existing + session.seconds);
    });
    return map;
  }, [normalizedSessions]);

  const dailyChartData = useMemo<ChartPoint[]>(() => {
    if (dayTotalsMap.size === 0) return [];
    return Array.from(dayTotalsMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, seconds]) => ({
        dateKey,
        label: formatChartDayLabel(dateKey, true),
        fullLabel: formatFullDate(dateKey),
        minutes: seconds / 60,
      }));
  }, [dayTotalsMap]);

  const monthlyChartData = useMemo<ChartPoint[]>(() => {
    if (normalizedSessions.length === 0) return [];
    const map = new Map<string, number>();
    normalizedSessions.forEach((session) => {
      const key = `${session.date.getUTCFullYear()}-${String(
        session.date.getUTCMonth() + 1
      ).padStart(2, '0')}`;
      map.set(key, (map.get(key) ?? 0) + session.seconds);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, seconds]) => {
        const [yearStr, monthStr] = key.split('-');
        const date = new Date(
          Date.UTC(Number(yearStr), Number(monthStr) - 1, 1)
        );
        return {
          dateKey: key,
          label: new Intl.DateTimeFormat(undefined, {
            month: 'short',
          }).format(date),
          fullLabel: new Intl.DateTimeFormat(undefined, {
            month: 'long',
            year: 'numeric',
          }).format(date),
          minutes: seconds / 60,
        };
      })
      .slice(-12);
  }, [normalizedSessions]);

  const yearlyChartData = useMemo<ChartPoint[]>(() => {
    if (normalizedSessions.length === 0) return [];
    const map = new Map<number, number>();
    normalizedSessions.forEach((session) => {
      const year = session.date.getUTCFullYear();
      map.set(year, (map.get(year) ?? 0) + session.seconds);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a - b)
      .map(([year, seconds]) => ({
        dateKey: String(year),
        label: year.toString(),
        fullLabel: year.toString(),
        minutes: seconds / 60,
      }));
  }, [normalizedSessions]);

  const chartData = useMemo<ChartPoint[]>(() => {
    switch (granularity) {
      case 'month':
        return monthlyChartData;
      case 'year':
        return yearlyChartData;
      default:
        return dailyChartData;
    }
  }, [granularity, dailyChartData, monthlyChartData, yearlyChartData]);

  const bestDay = useMemo(() => {
    if (dayTotalsMap.size === 0) return null;
    let winnerKey: string | null = null;
    let winnerSeconds = 0;

    dayTotalsMap.forEach((seconds, key) => {
      if (seconds > winnerSeconds) {
        winnerSeconds = seconds;
        winnerKey = key;
      } else if (seconds === winnerSeconds && winnerKey && key > winnerKey) {
        winnerKey = key;
      }
    });

    if (!winnerKey) return null;

    return {
      dateKey: winnerKey,
      seconds: winnerSeconds,
    };
  }, [dayTotalsMap]);

  const weeklyPoints = useMemo<WeeklyPoint[]>(() => {
    if (normalizedSessions.length === 0) return [];

    const map = new Map<string, number>();
    normalizedSessions.forEach((session) => {
      const weekStart = getWeekStart(session.date).toISOString().slice(0, 10);
      map.set(weekStart, (map.get(weekStart) ?? 0) + session.seconds);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStart, seconds]) => ({
        weekStart,
        minutes: seconds / 60,
      }))
      .slice(-8);
  }, [normalizedSessions]);

  const totalSecondsAllTime = useMemo(
    () =>
      normalizedSessions.reduce(
        (sum, session) => sum + (Number(session.seconds) || 0),
        0
      ),
    [normalizedSessions]
  );

  const totalSessions = sessions.length;

  const longestSessionSeconds = useMemo(
    () =>
      normalizedSessions.reduce(
        (max, session) => Math.max(max, session.seconds),
        0
      ),
    [normalizedSessions]
  );

  const streaks = useMemo(() => computeStreaks(daySet), [daySet]);

  const monthlySummary = useMemo(
    () => computeMonthlySummary(normalizedSessions),
    [normalizedSessions]
  );

  const averageSessionMinutes =
    totalSessions > 0 ? totalSecondsAllTime / totalSessions / 60 : 0;

  const weeklyTrend = useMemo(
    () => computeWeeklyTrend(weeklyPoints),
    [weeklyPoints]
  );

  const isFetching = authLoading || loading;
  const hasSessions = normalizedSessions.length > 0;
  const chartsWrapperRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);

  const chartTitle = useMemo(() => {
    switch (granularity) {
      case 'month':
        return 'Minutes per month';
      case 'year':
        return 'Minutes per year';
      default:
        return 'Minutes per day';
    }
  }, [granularity]);

  useLayoutEffect(() => {
    const wrapper = chartsWrapperRef.current;
    if (!wrapper) return;
    const scrollEl = wrapper.querySelector(
      '.overflow-x-auto'
    ) as HTMLDivElement | null;
    if (!scrollEl) return;

    if (granularity !== 'day') {
      // Reset so that if the user switches back to 'day', we auto-scroll again.
      hasAutoScrolledRef.current = false;
      return;
    }

    if (!hasAutoScrolledRef.current) {
      scrollEl.scrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
      hasAutoScrolledRef.current = true;
    }
  }, [chartData.length, chartType, granularity]);
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
      />

      <Container className="relative z-10 pb-20 pt-8 sm:pt-16">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-3xl font-bold sm:text-4xl lg:text-5xl">
            Your Breathing Stats
          </h1>
          <p className="text-base text-gray-600 sm:text-lg">
            Dive deeper into your sessions, streaks, and trends. These numbers
            update every time you breathe with{' '}
            <span className="logo-font font-bold">Xhalin</span>.
          </p>
        </div>

        {error && (
          <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {!isFetching && !hasSessions && !error ? (
          <div className="mx-auto mt-10 max-w-3xl text-center">
            <EmptyState message="Start your first breathing session to unlock your personalized stats.">
              <div className="mt-4">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                  className="begin-breathing-button"
                >
                  Begin Breathing
                </Button>
              </div>
            </EmptyState>
          </div>
        ) : null}

        <div className="mt-8 grid gap-4 sm:mt-12 sm:gap-6 lg:grid-cols-3">
          <div className="card flex min-w-0 flex-col gap-4 lg:col-span-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="text-center sm:text-left">
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                  {chartTitle}
                </h2>
                <p className="text-sm text-gray-500">
                  All time ·{' '}
                  {
                    GRANULARITY_OPTIONS.find(
                      (option) => option.id === granularity
                    )?.label
                  }{' '}
                  view
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex flex-wrap gap-2">
                  {GRANULARITY_OPTIONS.map((option) => (
                    <Button
                      key={option.id}
                      type="button"
                      size="sm"
                      variant="secondary"
                      aria-pressed={granularity === option.id}
                      onClick={() => setGranularity(option.id)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 sm:ml-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={chartType === 'bar' ? 'secondary' : 'outline'}
                    aria-pressed={chartType === 'bar'}
                    onClick={() => setChartType('bar')}
                  >
                    Bar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={chartType === 'line' ? 'secondary' : 'outline'}
                    aria-pressed={chartType === 'line'}
                    onClick={() => setChartType('line')}
                  >
                    Line
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-6 min-w-0" ref={chartsWrapperRef}>
              {isFetching ? (
                <SectionLoading message="Loading your breathing time..." />
              ) : chartData.length === 0 ? (
                <EmptyState message="No sessions recorded yet for this view." />
              ) : chartType === 'bar' ? (
                <TimelineBarChart data={chartData} />
              ) : (
                <TimelineLineChart data={chartData} />
              )}
            </div>
          </div>

          <div className="card flex flex-col justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 sm:text-sm">
                Best Day
              </p>
              <h2 className="mt-2 text-4xl font-extrabold text-gray-900 sm:text-5xl lg:text-6xl">
                {bestDay ? formatMinutesShort(bestDay.seconds / 60) : '--'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {bestDay
                  ? formatFullDate(bestDay.dateKey)
                  : 'Complete a session to set a record.'}
              </p>
            </div>
            <div
              className="mt-4 rounded-2xl border border-white/30 p-4 text-center text-gray-900 shadow-inner backdrop-blur-sm"
              style={{ backgroundColor: '#dff9fa' }}
            >
              <p className="text-sm text-gray-600">Need a target?</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                Repeat your best day or go even longer today.
              </p>
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Begin Breathing
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-3">
          <div className="card flex flex-col gap-4">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 sm:text-sm">
                  Weekly Trends
                </p>
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                  Last {weeklyPoints.length || 0} weeks
                </h2>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  weeklyTrend.direction === 'up'
                    ? 'bg-emerald-50 text-emerald-700'
                    : weeklyTrend.direction === 'down'
                      ? 'bg-rose-50 text-rose-700'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                {weeklyTrend.direction === 'up'
                  ? 'Trend Up'
                  : weeklyTrend.direction === 'down'
                    ? 'Trend Down'
                    : 'Holding steady'}
              </span>
            </div>
            <div className="mt-4">
              {isFetching ? (
                <SectionLoading message="Collecting weekly patterns..." />
              ) : weeklyPoints.length === 0 ? (
                <EmptyState message="Add sessions to see your weekly trend line." />
              ) : (
                <>
                  <WeeklySparkline points={weeklyPoints} />
                  <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                    <div className="rounded-2xl bg-white/80 p-4 shadow-inner">
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        Last week
                      </p>
                      <p className="mt-1 text-xl font-semibold text-gray-900">
                        {formatMinutesShort(weeklyTrend.current)}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white/80 p-4 shadow-inner">
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        Change vs. prior week
                      </p>
                      <p
                        className={`mt-1 text-xl font-semibold ${
                          weeklyTrend.change > 0
                            ? 'text-emerald-600'
                            : weeklyTrend.change < 0
                              ? 'text-rose-600'
                              : 'text-gray-800'
                        }`}
                      >
                        {weeklyTrend.change === 0
                          ? 'No change'
                          : `${weeklyTrend.change > 0 ? '+' : '-'}${formatDeltaMinutes(
                              Math.abs(weeklyTrend.change)
                            )}`}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card lg:col-span-2">
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 sm:text-sm">
                  Monthly Summary
                </p>
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                  {new Intl.DateTimeFormat(undefined, {
                    month: 'long',
                    year: 'numeric',
                  }).format(new Date())}
                </h2>
              </div>
            </div>
            <div className="mt-4">
              {isFetching ? (
                <SectionLoading message="Summarizing this month's sessions..." />
              ) : monthlySummary.sessions === 0 ? (
                <EmptyState message="No sessions logged yet this month." />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <MetricCard
                    label="Total minutes"
                    value={formatMinutesShort(monthlySummary.totalMinutes)}
                    helper="Time invested this month"
                    className="border border-white/30 shadow-inner backdrop-blur-sm"
                    style={{ backgroundColor: '#dff9fa' }}
                  />
                  <MetricCard
                    label="Sessions completed"
                    value={monthlySummary.sessions.toString()}
                    helper="Consistency is building"
                    className="border border-white/30 shadow-inner backdrop-blur-sm"
                    style={{ backgroundColor: '#dff9fa' }}
                  />
                  <MetricCard
                    label="Avg. time per session"
                    value={formatMinutesShort(monthlySummary.averageMinutes)}
                    helper="Typical focus window"
                    className="border border-white/30 shadow-inner backdrop-blur-sm"
                    style={{ backgroundColor: '#dff9fa' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card mt-4 flex flex-col gap-4 sm:mt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 sm:text-sm">
                All-Time Stats
              </p>
              <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                Lifetime insights
              </h2>
            </div>
          </div>
          <div className="mt-4">
            {isFetching ? (
              <SectionLoading message="Gathering your all-time history..." />
            ) : !hasSessions ? (
              <EmptyState message="Once you complete a session, your all-time dashboard will appear here." />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
                <MetricCard
                  label="Total time"
                  value={formatDuration(totalSecondsAllTime)}
                  helper="Across every recorded session"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
                <MetricCard
                  label="Total sessions"
                  value={totalSessions.toString()}
                  helper="Moments of mindful breathing"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
                <MetricCard
                  label="Longest session"
                  value={formatDuration(longestSessionSeconds)}
                  helper="Peak single-session focus"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
                <MetricCard
                  label="Longest streak"
                  value={`${streaks.longest} day${streaks.longest === 1 ? '' : 's'}`}
                  helper="Most consecutive days"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
                <MetricCard
                  label="Current streak"
                  value={`${streaks.current} day${
                    streaks.current === 1 ? '' : 's'
                  }`}
                  helper="Keep the momentum going"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
                <MetricCard
                  label="Avg. session length"
                  value={formatMinutesShort(averageSessionMinutes)}
                  helper="Across your full history"
                  className="border border-white/30 shadow-inner backdrop-blur-sm"
                  style={{ backgroundColor: '#dff9fa' }}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}

function TimelineBarChart({ data }: { data: ChartPoint[] }) {
  if (data.length === 0) return null;
  const maxMinutes = Math.max(...data.map((point) => point.minutes), 0);
  const safeMax = Math.max(maxMinutes, 1);

  return (
    <div
      className="-mx-2 flex min-w-0 max-w-full items-end gap-2 overflow-x-auto px-2 pb-3 sm:gap-3 sm:pb-4"
      style={{ minHeight: `${MAX_BAR_HEIGHT + 20}px`, maxWidth: '100%' }}
    >
      {data.map((bar) => {
        const height = (bar.minutes / safeMax) * MAX_BAR_HEIGHT;
        return (
          <div
            key={bar.dateKey}
            className="flex min-w-[40px] flex-col items-center text-center text-xs text-gray-500 sm:min-w-[48px]"
            title={`${bar.fullLabel} · ${formatMinutesShort(bar.minutes)}`}
          >
            <span className="mb-1 text-[10px] font-semibold text-gray-800 sm:mb-2 sm:text-[11px]">
              {formatMinutesCompact(bar.minutes)}
            </span>
            <div className="flex h-full w-8 items-end justify-center sm:w-10">
              <div
                className="w-2.5 rounded-full bg-gradient-to-b from-primary-200 via-primary-500 to-secondary-500 transition-[height] duration-500 ease-out sm:w-3"
                style={{ height: `${Math.max(height, 4)}px` }}
              />
            </div>
            <span className="mt-1 text-[10px] text-gray-600 sm:mt-2 sm:text-[11px]">
              {bar.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function WeeklySparkline({ points }: { points: WeeklyPoint[] }) {
  const strokeId = useId();
  const fillId = `${strokeId}-fill`;
  const height = 60;

  if (points.length === 0) return null;

  const maxMinutes = Math.max(...points.map((point) => point.minutes), 1);
  const coords = points.map((point, index) => ({
    x: points.length === 1 ? 0 : (index / (points.length - 1)) * 100,
    y: height - (point.minutes / maxMinutes) * (height - 12) - 6,
  }));

  const linePath = coords
    .map(
      (coord, index) =>
        `${index === 0 ? 'M' : 'L'}${coord.x.toFixed(2)},${coord.y.toFixed(2)}`
    )
    .join(' ');
  const areaPath = `${linePath} L100,${height} L0,${height} Z`;

  return (
    <svg viewBox={`0 0 100 ${height}`} className="h-28 w-full">
      <defs>
        <linearGradient id={strokeId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5FC5D6" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
        <linearGradient id={fillId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#5FC5D6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${fillId})`} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={`url(#${strokeId})`}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      {coords.map((coord, index) => (
        <circle
          key={`${coord.x}-${index}`}
          cx={coord.x}
          cy={coord.y}
          r={1.5}
          fill="#7C3AED"
          stroke="#fff"
          strokeWidth={0.4}
        />
      ))}
    </svg>
  );
}

function MetricCard({
  label,
  value,
  helper,
  className,
  style,
}: {
  label: string;
  value: string;
  helper?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const base =
    'rounded-2xl border border-white/70 bg-white/85 p-4 shadow-sm backdrop-blur-sm';
  return (
    <div className={`${base} ${className ?? ''}`} style={style}>
      <p className="text-[10px] uppercase tracking-wide text-gray-500 sm:text-xs">
        {label}
      </p>
      <p className="mt-2 text-xl font-semibold text-gray-900 sm:text-2xl">
        {value}
      </p>
      {helper ? (
        <p className="mt-1 text-xs text-gray-500 sm:text-sm">{helper}</p>
      ) : null}
    </div>
  );
}

function EmptyState({
  message,
  children,
}: {
  message: string;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-300 bg-white/70 px-6 py-10 text-center text-sm text-gray-600">
      <div>{message}</div>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

function SectionLoading({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-primary-100 bg-white/70 px-6 py-10 text-center text-sm text-primary-700">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-200 border-t-primary-500" />
      <p>{message}</p>
    </div>
  );
}

function formatChartDayLabel(dateKey: string, includeMonth: boolean) {
  const date = parseDateKey(dateKey);
  return new Intl.DateTimeFormat(undefined, {
    month: includeMonth ? 'short' : undefined,
    day: 'numeric',
  }).format(date);
}

function formatFullDate(dateKey: string) {
  const date = parseDateKey(dateKey);
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatDateKey(date: Date) {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}

function parseDateKey(dateKey: string) {
  const parts = dateKey.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  return new Date(year, month - 1, day);
}

function formatMinutesShort(minutes: number) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m';
  if (minutes < 1) return '<1m';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remaining = Math.round(minutes - hours * 60);
    if (remaining <= 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remaining}m`;
  }

  if (minutes < 10) {
    return `${minutes.toFixed(1)}m`;
  }

  return `${Math.round(minutes)}m`;
}

function formatMinutesCompact(minutes: number) {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m';
  if (minutes >= 90) {
    const hours = Math.round(minutes / 60);
    return `${hours}h`;
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remaining = Math.round(minutes - hours * 60);
    return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
  }
  if (minutes < 5) {
    return `${minutes.toFixed(1)}m`;
  }
  return `${Math.round(minutes)}m`;
}

function formatDeltaMinutes(minutes: number) {
  if (!Number.isFinite(minutes)) return '0m';
  if (minutes < 1) return '<1m';
  if (minutes < 10) return `${minutes.toFixed(1)}m`;
  return `${Math.round(minutes)}m`;
}

function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  const parts: string[] = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (!hours && !minutes && secs) parts.push(`${secs}s`);

  return parts.join(' ');
}

function getWeekStart(date: Date) {
  const utc = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const day = utc.getUTCDay(); // 0 (Sun) - 6 (Sat)
  const diff = (day + 6) % 7; // convert to Monday start
  utc.setUTCDate(utc.getUTCDate() - diff);
  return utc;
}

function buildDaySet(records: BreathingSession[]) {
  const set = new Set<string>();
  if (!records) return set;
  records.forEach((session) => {
    const rawDate = session.session_date || session.created_at;
    if (!rawDate) return;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return;
    set.add(formatDateKey(date));
  });
  return set;
}

function computeMonthlySummary(sessions: NormalizedSession[]) {
  if (!sessions || sessions.length === 0) {
    return { totalMinutes: 0, sessions: 0, averageMinutes: 0 };
  }
  const now = new Date();
  const targetYear = now.getUTCFullYear();
  const targetMonth = now.getUTCMonth();

  const filtered = sessions.filter(
    (session) =>
      session.date.getUTCFullYear() === targetYear &&
      session.date.getUTCMonth() === targetMonth
  );
  const totalSeconds = filtered.reduce(
    (sum, session) => sum + session.seconds,
    0
  );
  const sessionsCount = filtered.length;
  return {
    totalMinutes: totalSeconds / 60,
    sessions: sessionsCount,
    averageMinutes: sessionsCount > 0 ? totalSeconds / sessionsCount / 60 : 0,
  };
}

// computeStreaks and calculateCurrentStreak are provided by @lib/streaks

function computeWeeklyTrend(points: WeeklyPoint[]) {
  const baseTrend = {
    direction: 'flat' as const,
    change: 0,
    current: 0,
    previous: 0,
  };
  if (points.length === 0) {
    return baseTrend;
  }

  const lastIndex = points.length - 1;
  const currentPoint = points[lastIndex];
  if (!currentPoint) {
    return baseTrend;
  }

  const previousPoint = lastIndex > 0 ? points[lastIndex - 1] : currentPoint;
  if (!previousPoint) {
    return baseTrend;
  }

  const current = currentPoint.minutes;
  const previous = previousPoint.minutes;
  const change = current - previous;
  let direction: 'up' | 'down' | 'flat' = 'flat';
  if (change > 1) direction = 'up';
  else if (change < -1) direction = 'down';

  return { direction, change, current, previous };
}

export default Stats;
